import type { ColorSource, Graphics } from 'pixi.js';
import { WIRE_PIXEL_GRID } from './wireGeometry';

/** Built-in artwork uses 25 logical pixels inside the existing 120-unit cell.
 * Keep this separate from PixelArtData: saved, user-authored art remains 25×25.
 */
export const TILE_PIXEL_GRID = WIRE_PIXEL_GRID;
export const TILE_PIXEL_SIZE = 120 / TILE_PIXEL_GRID;

export function pixelRect(
  g: Graphics,
  x: number,
  y: number,
  width: number,
  height: number,
  color: ColorSource,
) {
  g.rect(
    x * TILE_PIXEL_SIZE,
    y * TILE_PIXEL_SIZE,
    width * TILE_PIXEL_SIZE,
    height * TILE_PIXEL_SIZE,
  ).fill(color);
}

/** Hand-authored masks, merged into horizontal runs; no texture or rasterization step. */
export function pixelMask(
  g: Graphics,
  rows: readonly string[],
  x: number,
  y: number,
  color: ColorSource,
  turn = 0,
) {
  rows.forEach((row, dy) => {
    for (let dx = 0; dx < row.length; dx++) {
      if (row[dx] !== '#') continue;
      const start = dx;
      while (row[dx + 1] === '#') dx++;
      const length = dx - start + 1,
        px = x + start,
        py = y + dy;
      if (turn === 1) pixelRect(g, TILE_PIXEL_GRID - 1 - py, px, 1, length, color);
      else if (turn === 2)
        pixelRect(
          g,
          TILE_PIXEL_GRID - px - length,
          TILE_PIXEL_GRID - 1 - py,
          length,
          1,
          color,
        );
      else if (turn === 3)
        pixelRect(g, py, TILE_PIXEL_GRID - px - length, 1, length, color);
      else pixelRect(g, px, py, length, 1, color);
    }
  });
}

// Authored at 25px: two-pixel strokes, finer stepped curves and open counters.
export const TILE_SYMBOLS = {
  diode: [
    '#..............',
    '###............',
    '#####..........',
    '#######........',
    '#########......',
    '###########....',
    '#############..',
    '###############',
    '#############..',
    '###########....',
    '#########......',
    '#######........',
    '#####..........',
    '###............',
    '#..............',
  ],
  inverter: [
    '#............',
    '##...........',
    '####.........',
    '######.......',
    '########.....',
    '##########...',
    '############.',
    '#############',
    '############.',
    '##########...',
    '########.....',
    '######.......',
    '####.........',
    '##...........',
    '#............',
  ],
  bubble: ['.###.', '##.##', '#...#', '##.##', '.###.'],
  and: [
    '##...........##',
    '###.........###',
    '.###.......###.',
    '..###.....###..',
    '...###...###...',
    '....###.###....',
    '.....#####.....',
    '......###......',
    '.....#####.....',
    '....###.###....',
    '...###...###...',
    '..###.....###..',
    '.###.......###.',
    '###.........###',
    '##...........##',
  ],
  clock: [
    '.....#####.....',
    '...#########...',
    '..###.....###..',
    '.###.......###.',
    '.##....##...##.',
    '##.....##....##',
    '##.....##....##',
    '##.....########',
    '##...........##',
    '##...........##',
    '.##.........##.',
    '.###.......###.',
    '..###.....###..',
    '...#########...',
    '.....#####.....',
  ],
  // Rounded shuffle paths; a small overpass gap keeps the crossing legible.
  random: [
    '.................',
    '.............##..',
    '#####.......####.',
    '######.....######',
    '....###...######.',
    '.....###.###.##..',
    '......#..##......',
    '........##.......',
    '.......###.......',
    '.......##........',
    '......##..#......',
    '.....###.###.##..',
    '....###...######.',
    '######.....######',
    '#####.......####.',
    '.............##..',
    '.................',
  ],
  button: [
    '......###########......',
    '...#################...',
    '..###################..',
    '.#####################.',
    '.#####################.',
    '.#####################.',
    '#######################',
    '#######################',
    '#######################',
    '#######################',
    '#######################',
    '#######################',
    '#######################',
    '#######################',
    '#######################',
    '#######################',
    '#######################',
    '.#####################.',
    '.#####################.',
    '.#####################.',
    '..###################..',
    '...#################...',
    '......###########......',
  ],
} as const;

export function pixelControl(
  g: Graphics,
  color: ColorSource,
  button: boolean,
  high: boolean,
) {
  if (button) {
    pixelMask(g, TILE_SYMBOLS.button, 1, 1, 0x000000);
    pixelMask(
      g,
      [
        '....###########....',
        '..###############..',
        '.#################.',
        '.#################.',
        '###################',
        '###################',
        '###################',
        '###################',
        '###################',
        '###################',
        '###################',
        '###################',
        '###################',
        '###################',
        '###################',
        '.#################.',
        '.#################.',
        '..###############..',
        '....###########....',
      ],
      3,
      3,
      color,
    );
  } else {
    pixelRect(g, 1, 1, 23, 23, 0x000000);
    pixelRect(g, 3, 3, 19, 19, color);
    pixelRect(g, 1, high ? 16 : 1, 23, 8, 0xffffff);
  }
}
